import type { Bet, BetslipView } from '@nsftx/seven-client-retail-vue-integrator'
import type { NotificationPayload } from '@nsftx/seven-client-retail-vue-sgp'
import {
  recordBetslipCancelTransaction,
  recordBetslipCashoutTransaction,
  recordBetslipPayoutTransaction,
} from './calculatorTransactionService'

type SlipActivity = BetslipView['activities'][number]
type SlipUpdateNotification = NotificationPayload<'ng::slip-update'>

const SLIP_UPDATE_TOPIC = 'ng::slip-update'

const isActivityFinished = (activity: SlipActivity, finishedState: string) => {
  if (activity.currentState === finishedState) {
    return true
  }

  if (activity.finishedAt) {
    return true
  }

  return activity.states?.some(({ state }) => state === finishedState) ?? false
}

type ResettleActivityBet = NonNullable<
  Extract<SlipActivity, { type: 'RESETTLE_BETS' }>['bets']
>[number]

const mergeResettleActivityBet = (slipBet: Bet, activityBet: ResettleActivityBet): Bet => ({
  ...slipBet,
  winnings: activityBet.winnings ?? slipBet.winnings,
})

const resolveBetForActivity = (
  slip: BetslipView,
  activity: SlipActivity,
  betId: string,
): Bet | undefined => {
  const slipBet = slip.bets.find(({ id }) => id === betId)

  if (activity.type !== 'RESETTLE_BETS' || !activity.bets?.length) {
    return slipBet
  }

  const activityBet = activity.bets.find((bet) => bet.id === betId)

  if (!slipBet) {
    return undefined
  }

  if (!activityBet) {
    return slipBet
  }

  return mergeResettleActivityBet(slipBet, activityBet)
}

const recordBetPayouts = (slip: BetslipView, activity: SlipActivity, betIds: string[]) => {
  const currency = slip.context.currency
  const linkCode = slip.context.linkCode

  for (const betId of betIds) {
    const bet = resolveBetForActivity(slip, activity, betId)

    if (!bet) {
      continue
    }

    if (bet.status.resolutionStatus === 'LOST') {
      continue
    }

    recordBetslipPayoutTransaction(slip.id, bet, currency, linkCode)
  }
}

const syncWonBetsFromSlip = (slip: BetslipView) => {
  const currency = slip.context.currency
  const linkCode = slip.context.linkCode

  for (const bet of slip.bets) {
    if (bet.status.resolutionStatus !== 'WON') {
      continue
    }

    recordBetslipPayoutTransaction(slip.id, bet, currency, linkCode)
  }
}

const processSlipActivity = (slip: BetslipView, activity: SlipActivity) => {
  switch (activity.type) {
    case 'PAYOUT_BETS':
      if (isActivityFinished(activity, 'PAYOUT_BETS_FINISHED')) {
        recordBetPayouts(slip, activity, activity.payoutBetIds ?? [])
      }
      break
    case 'SETTLE_BETS':
      if (isActivityFinished(activity, 'SETTLE_BETS_FINISHED')) {
        recordBetPayouts(slip, activity, activity.settleBetIds ?? [])
      }
      break
    case 'RESETTLE_BETS':
      if (isActivityFinished(activity, 'RESETTLE_BETS_FINISHED')) {
        recordBetPayouts(slip, activity, activity.betIds ?? [])
      }
      break
    case 'CANCEL_BETS':
      if (isActivityFinished(activity, 'CANCEL_BETS_FINISHED')) {
        const currency = slip.context.currency
        const linkCode = slip.context.linkCode

        for (const betId of activity.cancelBetIds ?? []) {
          const bet = slip.bets.find(({ id }) => id === betId)

          if (!bet) {
            continue
          }

          recordBetslipCancelTransaction(slip.id, bet, currency, linkCode)
        }
      }
      break
    case 'CASHOUT_BETS':
      if (isActivityFinished(activity, 'CASHOUT_BETS_FINISHED')) {
        const currency = slip.context.currency
        const linkCode = slip.context.linkCode

        for (const betId of activity.cashoutBetIds ?? []) {
          const bet = slip.bets.find(({ id }) => id === betId)

          if (!bet) {
            continue
          }

          recordBetslipCashoutTransaction(slip.id, bet, currency, linkCode)
        }
      }
      break
    default:
      break
  }
}

export const syncCalculatorFromBetslip = (slip: BetslipView) => {
  for (const slipActivity of slip.activities) {
    processSlipActivity(slip, slipActivity)
  }

  syncWonBetsFromSlip(slip)
}

export const handleSlipUpdate = (detail: SlipUpdateNotification) => {
  const { activity, slip } = detail.content

  processSlipActivity(slip, activity)

  for (const slipActivity of slip.activities) {
    if (slipActivity.id === activity.id) {
      continue
    }

    processSlipActivity(slip, slipActivity)
  }

  syncWonBetsFromSlip(slip)
}

let slipUpdateListener: ((event: Event) => void) | undefined

export const registerCalculatorSlipNotificationHandlers = () => {
  if (slipUpdateListener) {
    return
  }

  slipUpdateListener = (event: Event) => {
    handleSlipUpdate((event as CustomEvent<SlipUpdateNotification>).detail)
  }

  window.addEventListener(SLIP_UPDATE_TOPIC, slipUpdateListener)
}

export const unregisterCalculatorSlipNotificationHandlers = () => {
  if (!slipUpdateListener) {
    return
  }

  window.removeEventListener(SLIP_UPDATE_TOPIC, slipUpdateListener)
  slipUpdateListener = undefined
}
